import { useEffect, useRef, useState } from "react";
import { FormattedMessage as FM } from "react-intl";

import ImageQr from "components/Tools/ImageQr";
import { ArrowDownIcon, CopyIcon, LinkIcon } from "components/Icons";
import { cn } from "tools/ui";
import { copyText, saveQrImage } from "../helpers/qr";
import {
  QR_FEEDBACK_TIME,
  STYLE_ACTION_BUTTON,
  STYLE_ACTION_BUTTON_PRIMARY,
  STYLE_QR_FRAME,
} from "../constants";

const feedbackOk = "text-emerald-700";
const feedbackError = "text-red-700";

function QrCard({ qr, url }) {
  const frame = useRef(null);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    if (!feedback?.ok) return undefined;
    const timer = setTimeout(() => setFeedback(null), QR_FEEDBACK_TIME);
    return () => clearTimeout(timer);
  }, [feedback]);

  function handleDownload() {
    const ok = saveQrImage(frame.current, qr.fileName);
    setFeedback({
      ok,
      id: ok ? "melhous.dash.qr.downloaded" : "melhous.dash.qr.download_error",
    });
  }

  async function handleCopy() {
    const ok = await copyText(url);
    setFeedback({
      ok,
      id: ok ? "melhous.dash.qr.copied" : "melhous.dash.qr.copy_error",
    });
  }

  return (
    <article className="flex min-w-0 flex-col gap-3 rounded-lg border border-neutral-200 bg-white p-4 shadow-sm">
      <div>
        <h2 className="font-semibold text-neutral-800">
          <FM id={qr.label} />
        </h2>
        <p className="text-sm text-neutral-500">
          <FM id={qr.hint} />
        </p>
      </div>
      <div ref={frame} className={STYLE_QR_FRAME}>
        <ImageQr value={url} name={qr.name} />
      </div>
      <div className="flex min-w-0 items-center gap-2 rounded-md bg-neutral-100 py-1 pl-3 pr-1">
        <LinkIcon width={14} className="shrink-0 fill-neutral-500" />
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          title={url}
          className="min-w-0 flex-1 truncate text-xs text-sky-900 underline"
        >
          {url}
        </a>
        <button
          type="button"
          onClick={handleCopy}
          className={cn(
            STYLE_ACTION_BUTTON,
            "flex shrink-0 items-center gap-1 !py-1",
          )}
        >
          <CopyIcon width={14} className="fill-current" />
          <FM id="melhous.dash.qr.copy" />
        </button>
      </div>
      <button
        type="button"
        onClick={handleDownload}
        className={cn(
          STYLE_ACTION_BUTTON_PRIMARY,
          "flex w-full items-center justify-center gap-2",
        )}
      >
        <ArrowDownIcon width={16} className="fill-current" />
        <FM id="melhous.dash.qr.download" />
      </button>
      <p
        role="status"
        className={cn(
          "min-h-4 text-center text-xs",
          feedback?.ok ? feedbackOk : feedbackError,
        )}
      >
        {feedback && <FM id={feedback.id} />}
      </p>
    </article>
  );
}

export default QrCard;
